import React, { useEffect, useState } from 'react';
import { FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove, UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, ChevronDown, Plus, Trash2 } from 'lucide-react';
import PhoneInput from '../ui/phone-input';
import { PatientFormData } from './patientForm.types';

type ContactField = FieldArrayWithId<PatientFormData, 'contacts', 'fieldId'>;

interface ContactInformationSectionProps {
  form: UseFormReturn<PatientFormData>;
  fields: ContactField[];
  append: UseFieldArrayAppend<PatientFormData, 'contacts'>;
  remove: UseFieldArrayRemove;
  createContact: (key: string) => { id: string; key: string; value: string; is_default?: boolean };
  isGuided?: boolean;
  /** When true, hide the required-marker styling on labels (Email, Phone). Phone is treated as optional in this mode. */
  hideRequiredMarkers?: boolean;
  phoneRequired?: boolean;
}

const ContactInformationSection = ({
  form,
  fields,
  append,
  remove,
  createContact,
  isGuided = false,
  hideRequiredMarkers = false,
  phoneRequired = true,
}: ContactInformationSectionProps) => {
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    const ensureDefault = (type: 'email' | 'phone') => {
      const currentContacts = form.getValues('contacts') || [];
      const hasDefault = currentContacts.some(
        contact => contact.key === type && contact.is_default
      );
      if (hasDefault) {
        return;
      }
      const firstIndex = currentContacts.findIndex(contact => contact.key === type);
      if (firstIndex === -1) {
        return;
      }
      const updatedContacts = currentContacts.map((contact, index) => {
        if (contact.key !== type) {
          return contact;
        }
        return { ...contact, is_default: index === firstIndex };
      });
      form.setValue('contacts', updatedContacts, {
        shouldDirty: false,
        shouldValidate: false,
      });
    };

    ensureDefault('email');
    ensureDefault('phone');
  }, [fields, form]);

  return (
    <div className='space-y-4'>
    <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>Contact Information</h3>
    {/* EMAIL CONTACT */}
    <div className='flex items-center justify-between gap-4'>
      <Label className={hideRequiredMarkers ? '' : 'required'}>Email Contact</Label>
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='h-7 rounded-full bg-brand-teal text-white hover:bg-brand-teal/80 hover:text-white px-3 py-1 text-sm font-normal'
        onClick={() => append(createContact('email'))}
      >
        <Plus className='mr-2 h-4 w-4' /> <span className='font-normal'>Add Email</span>
      </Button>
    </div>
    {fields
      .filter(field => field.key === 'email')
      .map((field) => {
        const contactIndex = fields.findIndex(item => item.id === field.id);
        if (contactIndex === -1) {
          return null;
        }
        const emailCount = fields.filter(f => f.key === 'email').length;
        const canDeleteEmail = emailCount > 1;

        const setDefaultEmail = () => {
          const currentContacts = form.getValues('contacts') || [];
          const updatedContacts = currentContacts.map((contact, index) => {
            if (contact.key !== 'email') {
              return contact;
            }
            return { ...contact, is_default: index === contactIndex };
          });
          form.setValue('contacts', updatedContacts, {
            shouldDirty: true,
            shouldValidate: true,
          });
        };

        return (
          <div key={field.fieldId} className='flex items-center gap-4'>
            <div className='flex flex-col w-full'>
              <Input
                {...form.register(`contacts.${contactIndex}.value`)}
                onChange={e => {
                  const value = e.target.value || '';
                  form.setValue(`contacts.${contactIndex}.value`, value.trim(), {
                    shouldValidate: true,
                  });
                }}
                placeholder='Enter email'
              />
              {form.formState.errors.contacts?.[contactIndex]?.value && (
                <p className='text-[0.8rem] text-red-500'>
                  {form.formState.errors.contacts[contactIndex].value?.message}
                </p>
              )}
            </div>

            <div className='flex items-center space-x-2'>
              <input
                type='radio'
                name='default-email'
                className='h-4 w-4 accent-brand-teal'
                checked={Boolean(form.watch(`contacts.${contactIndex}.is_default`))}
                onChange={setDefaultEmail}
              />
              <Label className='cursor-pointer text-xs font-normal'>Default</Label>
            </div>

            <div className='w-10'>
              <TooltipProvider delayDuration={100}>
                {canDeleteEmail && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='h-9 w-9 rounded-full border border-red-200 bg-transparent text-red-500 hover:border-red-500 hover:bg-red-500 hover:text-white'
                        onClick={() => remove(contactIndex)}
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side='left' className='border-slate-900 bg-slate-900 text-white'>
                      <p>Remove contact</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>
          </div>
        );
      })}

    {/* PHONE CONTACT */}
    <div className='flex items-center justify-between gap-4'>
      <Label className={hideRequiredMarkers || !phoneRequired ? '' : 'required'}>Phone Contact</Label>
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='h-7 rounded-full bg-brand-teal text-white hover:bg-brand-teal/80 hover:text-white px-3 py-1 text-sm font-normal'
        onClick={() => append(createContact('phone'))}
      >
        <Plus className='mr-2 h-4 w-4' /> <span className='font-normal'>Add Phone</span>
      </Button>
    </div>
    {fields
      .filter(field => field.key === 'phone')
      .map((field) => {
        const contactIndex = fields.findIndex(item => item.id === field.id);
        if (contactIndex === -1) {
          return null;
        }
        const phoneCount = fields.filter(f => f.key === 'phone').length;
        const canDeletePhone = phoneCount > 1;

        const setDefaultPhone = () => {
          const currentContacts = form.getValues('contacts') || [];
          const updatedContacts = currentContacts.map((contact, index) => {
            if (contact.key !== 'phone') {
              return contact;
            }
            return { ...contact, is_default: index === contactIndex };
          });
          form.setValue('contacts', updatedContacts, {
            shouldDirty: true,
            shouldValidate: true,
          });
        };

        return (
          <div key={field.fieldId} className='flex items-center gap-4'>
            <div className='w-full'>
              <PhoneInput
                form={form}
                name={`contacts.${contactIndex}.value`}
                placeholder='Enter contact'
                label={''}
                required={phoneRequired && !hideRequiredMarkers}
              />
            </div>

            <div className='flex items-center space-x-2'>
              <input
                type='radio'
                name='default-phone'
                className='h-4 w-4 accent-brand-teal'
                checked={Boolean(form.watch(`contacts.${contactIndex}.is_default`))}
                onChange={setDefaultPhone}
              />
              <Label className='cursor-pointer text-xs font-normal'>Default</Label>
            </div>

            <div className='w-10'>
              <TooltipProvider delayDuration={100}>
                {canDeletePhone && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='h-9 w-9 rounded-full border border-red-200 bg-transparent text-red-500 hover:border-red-500 hover:bg-red-500 hover:text-white'
                        onClick={() => remove(contactIndex)}
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side='left' className='border-slate-900 bg-slate-900 text-white'>
                      <p>Remove contact</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>
          </div>
        );
      })}

    {/* For guided clinics: collapse extra contact info behind "Add more contact methods" */}
    {isGuided ? (
      <>
        <button
          type='button'
          onClick={() => setShowMore(prev => !prev)}
          className='flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mt-2'
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showMore ? 'rotate-180' : ''}`} />
          {showMore ? 'Show fewer contact options' : 'Add more contact methods'}
        </button>

        {showMore && (
          <>
            {/* Preferred Contact Methods Section */}
            <div className='mt-4 space-y-4'>
              <div>
                <Label className='text-base font-medium'>Preferred Contact Methods</Label>
                <p className='m-0 text-xs leading-none text-slate-500'>
                  Select the patient's preferred way(s) to be contacted
                </p>
              </div>
              {!form.watch('preferred_contact_methods.email') &&
                !form.watch('preferred_contact_methods.sms') &&
                !form.watch('preferred_contact_methods.phone') && (
                  <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5'>
                    <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600 mt-0.5' />
                    <p className='text-xs text-amber-800'>
                      No preferences set. For a better experience, please select your preferred contact type.
                    </p>
                  </div>
                )}
              <div className='space-y-3'>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='contact-email'
                    checked={form.watch('preferred_contact_methods.email') || false}
                    onCheckedChange={checked => {
                      form.setValue('preferred_contact_methods.email', checked as boolean);
                    }}
                  />
                  <Label htmlFor='contact-email' className='cursor-pointer font-normal'>
                    Email
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='contact-sms'
                    checked={form.watch('preferred_contact_methods.sms') || false}
                    onCheckedChange={checked => {
                      form.setValue('preferred_contact_methods.sms', checked as boolean);
                    }}
                  />
                  <Label htmlFor='contact-sms' className='cursor-pointer font-normal'>
                    SMS
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='contact-phone'
                    checked={form.watch('preferred_contact_methods.phone') || false}
                    onCheckedChange={checked => {
                      form.setValue('preferred_contact_methods.phone', checked as boolean);
                    }}
                  />
                  <Label htmlFor='contact-phone' className='cursor-pointer font-normal'>
                    Phone Call
                  </Label>
                </div>
              </div>
            </div>

            {/* Emergency Contact Section */}
            <div className='mt-4 space-y-4'>
              <div>
                <Label className='text-base font-medium'>Emergency Contact</Label>
                <p className='m-0 text-xs leading-none text-slate-500'>
                  Phone number for emergency situations
                </p>
              </div>
              <PhoneInput
                form={form}
                name='emergency_contact_phone'
                placeholder='Enter emergency contact phone'
                label=''
                required={false}
              />
              <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5'>
                <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600 mt-0.5' />
                <div className='text-xs text-amber-800 space-y-1'>
                  <p>
                    This number will NOT be used for SMS, payment links, appointments, or routine communication.
                  </p>
                  <p>
                    Please enter the <strong>patient's primary phone number above</strong> for messaging and payments.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    ) : (
      <>
        {/* Preferred Contact Methods Section */}
        <div className='mt-6 space-y-4'>
          <div>
            <Label className='text-base font-medium'>Preferred Contact Methods</Label>
            <p className='m-0 text-xs leading-none text-slate-500'>
              Select the patient's preferred way(s) to be contacted
            </p>
          </div>
          {!form.watch('preferred_contact_methods.email') &&
            !form.watch('preferred_contact_methods.sms') &&
            !form.watch('preferred_contact_methods.phone') && (
              <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5'>
                <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600 mt-0.5' />
                <p className='text-xs text-amber-800'>
                  No preferences set. For a better experience, please select your preferred contact type.
                </p>
              </div>
            )}
          <div className='space-y-3'>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='contact-email'
                checked={form.watch('preferred_contact_methods.email') || false}
                onCheckedChange={checked => {
                  form.setValue('preferred_contact_methods.email', checked as boolean);
                }}
              />
              <Label htmlFor='contact-email' className='cursor-pointer font-normal'>
                Email
              </Label>
            </div>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='contact-sms'
                checked={form.watch('preferred_contact_methods.sms') || false}
                onCheckedChange={checked => {
                  form.setValue('preferred_contact_methods.sms', checked as boolean);
                }}
              />
              <Label htmlFor='contact-sms' className='cursor-pointer font-normal'>
                SMS
              </Label>
            </div>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='contact-phone'
                checked={form.watch('preferred_contact_methods.phone') || false}
                onCheckedChange={checked => {
                  form.setValue('preferred_contact_methods.phone', checked as boolean);
                }}
              />
              <Label htmlFor='contact-phone' className='cursor-pointer font-normal'>
                Phone Call
              </Label>
            </div>
          </div>
        </div>

        {/* Emergency Contact Section */}
        <div className='mt-6 space-y-4'>
          <div>
            <Label className='text-base font-medium'>Emergency Contact</Label>
            <p className='m-0 text-xs leading-none text-slate-500'>
              Phone number for emergency situations
            </p>
          </div>
          <PhoneInput
            form={form}
            name='emergency_contact_phone'
            placeholder='Enter emergency contact phone'
            label=''
            required={false}
          />
          <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5'>
            <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600 mt-0.5' />
            <div className='text-xs text-amber-800 space-y-1'>
              <p>
                This number will NOT be used for SMS, payment links, appointments, or routine communication.
              </p>
              <p>
                Please enter the <strong>patient's primary phone number above</strong> for messaging and payments.
              </p>
            </div>
          </div>
        </div>
      </>
    )}
  </div>
  );
};

export default ContactInformationSection;
